/**
 * DAYS WE ARE CLOSED. A holiday, a storm, a day nobody is available.
 *
 * The materialiser skips a closed day without shifting the series, so one skipped Thursday never
 * moves every Thursday after it. Closing a day does NOT touch visits already on it: each of those
 * was told to a client, so each moves through the move flow with its own record, and the Today
 * screen keeps showing them until somebody does that.
 *
 * Removing a day takes two taps, in words, rather than a browser confirm box that reads the same
 * for everything and is tapped through without being read.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { CLOSED_DATE_KINDS, CLOSED_DATE_KIND_LABELS, type ClosedDateKind } from '@fk/shared';
import {
  ApiError,
  addClosedDate,
  listClosedDates,
  removeClosedDate,
  type ClosedDateRow,
} from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button, SelectField, TextField } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';
import { formatDay, isDay, weekdayName } from '../format.ts';

export function ClosedDays() {
  const [rows, setRows] = useState<readonly ClosedDateRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    listClosedDates()
      .then((answer) => {
        if (live) setRows(answer.closedDates);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The closed days did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  return (
    <div>
      <ScreenTitle
        title="Days we are closed"
        subtitle="The standing schedule skips these without moving anybody's weekday."
      />

      <AddClosedDay onSaved={reload} />

      {error ? (
        <ErrorNotice
          title="The closed days did not load"
          message={error.message}
          onRetry={reload}
        />
      ) : rows === null ? (
        <Busy what="the closed days" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="No closed days coming up">
          <p>
            Add one above. Visits already booked on it stay where they are until they are moved.
          </p>
        </EmptyNotice>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id}>
              <ClosedDay row={row} onRemoved={reload} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClosedDay({ row, onRemoved }: { row: ClosedDateRow; onRemoved: () => void }) {
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  async function remove() {
    setRemoving(true);
    setProblem(null);
    try {
      await removeClosedDate(row.id);
      onRemoved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That day was not removed.');
      setRemoving(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-serif text-lg leading-tight">{row.label}</h2>
        <Pill>{CLOSED_DATE_KIND_LABELS[row.kind]}</Pill>
      </div>
      <p className="mt-1 text-sm text-navy/75">
        {weekdayName(row.closedOn)}, {formatDay(row.closedOn)}
      </p>

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not removed" message={problem} />
        </div>
      ) : null}

      <div className="mt-3">
        {asking ? (
          <div className="rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
            <p className="text-sm">
              Open {formatDay(row.closedOn)} again? The standing schedule will book visits on it the
              next time it runs.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={remove} disabled={removing}>
                {removing ? 'Removing' : 'Yes, we are open that day'}
              </Button>
              <Button tone="quiet" onClick={() => setAsking(false)} disabled={removing}>
                Keep it closed
              </Button>
            </div>
          </div>
        ) : (
          <Button tone="quiet" onClick={() => setAsking(true)}>
            Remove this day
          </Button>
        )}
      </div>
    </Card>
  );
}

function AddClosedDay({ onSaved }: { onSaved: () => void }) {
  const [closedOn, setClosedOn] = useState('');
  const [kind, setKind] = useState<ClosedDateKind>('holiday');
  const [label, setLabel] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (!isDay(closedOn)) {
      setProblem('Pick the day we are closed.');
      return;
    }
    if (!label.trim()) {
      setProblem(
        'Give the day a name, like Thanksgiving, so the gap on the schedule explains itself.',
      );
      return;
    }
    setSaving(true);
    try {
      await addClosedDate({ closedOn, kind, label: label.trim() });
      setClosedOn('');
      setLabel('');
      setKind('holiday');
      onSaved();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That day was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-5 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Close a day
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <TextField
          id="closed-on"
          label="Day"
          type="date"
          required
          value={closedOn}
          onChange={setClosedOn}
        />
        <SelectField
          id="closed-kind"
          label="Why"
          value={kind}
          onChange={setKind}
          options={CLOSED_DATE_KINDS}
          toLabel={(value) => CLOSED_DATE_KIND_LABELS[value]}
        />
        <TextField
          id="closed-label"
          label="What to call it"
          required
          value={label}
          onChange={setLabel}
          hint='"Christmas Day" reads better on the schedule than a bare gap.'
          autoComplete="off"
        />
        <p className={`mb-4 text-xs text-navy/65 ${MEASURE}`}>
          Visits already booked on this day are not touched. Each one was told to a client, so each
          moves from its own page, with its own message.
        </p>
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Close this day'}
        </Button>
      </form>
    </details>
  );
}
